import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Tooltip } from "@mantine/core"
import { formatClientLabel } from "../../../../lib/client-info"
import { formatDateTime } from "../../../../lib/format-date"
import { formatModelName, formatTranscriptionBackend } from "../../../../lib/provider-names"
import classes from "../../styles.module.css"
import type { IProps } from "./interfaces"

/**
 * SourceCard renders meeting metadata in the right-hand insights rail —
 * source / organiser / recorded-at / language. Each row collapses when its
 * underlying field is empty so we never show "Organiser: undefined".
 *
 * The source row prefers the captured client descriptor ("Desktop app · macOS ·
 * v0.1.5" / "Browser · Safari · macOS") and falls back to the legacy `source`
 * slug for meetings recorded before client capture landed (or Teams webhooks).
 */
export function SourceCard({ meeting }: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const sourceLabel = formatClientLabel(meeting.client_info, meeting.source)
  const backendLabel = formatTranscriptionBackend(meeting.transcription_backend)
  const modelLabel = formatModelName(meeting.llm_model)
  const modelTitle = meeting.llm_provider ? `${meeting.llm_provider} · ${meeting.llm_model}` : meeting.llm_model
  return (
    <section className={classes.card}>
      <div className={classes.cardHead}>
        <h2 className={classes.cardTitle}>
          <Trans>Source</Trans>
        </h2>
      </div>
      <div className={classes.metaList}>
        <div className={classes.metaListRow}>
          <span>
            <Trans>Recorded from</Trans>
          </span>
          <strong>{sourceLabel}</strong>
        </div>
        {meeting.organizer_name && (
          <div className={classes.metaListRow}>
            <span>
              <Trans>Organiser</Trans>
            </span>
            <strong>{meeting.organizer_name}</strong>
          </div>
        )}
        {meeting.start_time && (
          <div className={classes.metaListRow}>
            <span>
              <Trans>Recorded</Trans>
            </span>
            <strong>{formatDateTime(i18n, meeting.start_time)}</strong>
          </div>
        )}
        {meeting.analysis_locale && (
          <div className={classes.metaListRow}>
            <span>
              <Trans>Language</Trans>
            </span>
            <strong>{meeting.analysis_locale.toUpperCase()}</strong>
          </div>
        )}
        {backendLabel && (
          <div className={classes.metaListRow}>
            <span>
              <Trans>Transcription</Trans>
            </span>
            <Tooltip label={meeting.transcription_backend} withArrow disabled={!meeting.transcription_backend}>
              <strong>{backendLabel}</strong>
            </Tooltip>
          </div>
        )}
        {modelLabel && (
          <div className={classes.metaListRow}>
            <span>
              <Trans>Model</Trans>
            </span>
            <Tooltip label={modelTitle} withArrow disabled={!modelTitle}>
              <strong>{modelLabel}</strong>
            </Tooltip>
          </div>
        )}
      </div>
    </section>
  )
}
